import { FlexColumn, useStyles } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { useEffect, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue } from 'recoil';
import { streamingCurrentStateState, streamingState } from '../../../../storage/streaming';
import { MinimalApiAsset } from '../../../../types/asset';
import { ChannelId } from '../../../../types/gptTypes';
import { StreamingState, StreamType } from '../../../../types/streaming';
import StreamingChannelTag from '../StreamingChannelTag';
import StreamingPlayer from '../StreamingPlayer';

type Props = Readonly<{
  posters: {
    [ChannelId.dome]: string;
    [ChannelId.front]: string;
    [ChannelId.internal]: string;
  };
}>;

function StreamingWindow({ posters }: Props) {
  const { css, theme } = useStyles();
  const [streaming, setStreaming] = useRecoilState(streamingState);
  const internalPlayerRef = useRef<HTMLVideoElement>(null);
  const frontPlayerRef = useRef<HTMLVideoElement>(null);
  const domePlayerRef = useRef<HTMLVideoElement>(null);
  const currentState = useRecoilValue(streamingCurrentStateState);
  const asset: MinimalApiAsset | undefined =
    currentState?.asset?.subClient && currentState?.asset.client
      ? {
          assetId: currentState.asset._id,
          clientId: currentState.asset.client._id,
          subClientId: currentState.asset.subClient._id,
        }
      : undefined;
  const [loadedChannels, setLoadedChannels] = useState<ChannelId[]>([]);
  const channelsCount = currentState?.asset.channels;

  const videoLayerStyles = css({
    position: 'absolute',
    top: theme.sizing.scale200,
    right: theme.sizing.scale200,
    color: theme.colors.contentInversePrimary,
  });

  useEffect(() => {
    resetStuck();
  }, [streaming.ident]);

  const onBadRequest = (channel: ChannelId) => {
    setStuck(StreamingState.stuck, channel);
  };

  const onDisconnect = (channel: ChannelId) => {
    setStuck(StreamingState.stall, channel);
  };

  const onRefresh = (channel: ChannelId) => {
    setStuck(StreamingState.ok, channel);
  };

  const setStuck = (state: StreamingState, channel: ChannelId) => {
    setStreaming((prev) => ({
      ...prev,
      frontState: channel === ChannelId.front ? state : prev.frontState,
      internalState: channel === ChannelId.internal ? state : prev.internalState,
      domeState: channel === ChannelId.dome ? state : prev.domeState,
    }));
  };

  const resetStuck = () => {
    setStreaming((prev) => ({
      ...prev,
      frontStuck: false,
      internalStuck: false,
      domeStuck: false,
    }));
  };

  if (!streaming.ident || !asset || !channelsCount) {
    return null;
  }

  return (
    <FlexColumn classNames={css({ flex: 1 })}>
      <AutoSizer>
        {({ width, height }) => {
          return (
            <FlexColumn
              gap={theme.sizing.scale600}
              classNames={css({
                width: `${width}px`,
                height: `${height}px`,
                justifyContent: 'center',
              })}
            >
              <StreamingPlayer
                asset={asset}
                channel={ChannelId.internal}
                state={streaming.internalState}
                onDisconnect={() => onDisconnect(ChannelId.internal)}
                onBadRequest={() => onBadRequest(ChannelId.internal)}
                onRefresh={() => onRefresh(ChannelId.internal)}
                playerRef={internalPlayerRef}
                canLoad
                onLoadEnd={() => {
                  setLoadedChannels((prev) => [...prev, ChannelId.internal]);
                }}
                videoLayer={
                  <Block className={videoLayerStyles}>
                    <StreamingChannelTag channelId={ChannelId.internal} />
                  </Block>
                }
                poster={posters[ChannelId.internal]}
                streamType={StreamType.sub}
                maxWidth={width}
              />
              <StreamingPlayer
                asset={asset}
                channel={ChannelId.front}
                state={streaming.frontState}
                onDisconnect={() => onDisconnect(ChannelId.front)}
                onBadRequest={() => onBadRequest(ChannelId.front)}
                onRefresh={() => onRefresh(ChannelId.front)}
                playerRef={frontPlayerRef}
                canLoad={loadedChannels?.includes(ChannelId.internal)}
                onLoadEnd={() => {
                  setLoadedChannels((prev) => [...prev, ChannelId.front]);
                }}
                videoLayer={
                  <Block className={videoLayerStyles}>
                    <StreamingChannelTag channelId={ChannelId.front} />
                  </Block>
                }
                poster={posters[ChannelId.front]}
                streamType={StreamType.sub}
                maxWidth={width}
              />
              {channelsCount === 3 && (
                <StreamingPlayer
                  asset={asset}
                  channel={ChannelId.dome}
                  state={streaming.domeState}
                  onDisconnect={() => onDisconnect(ChannelId.dome)}
                  onBadRequest={() => onBadRequest(ChannelId.dome)}
                  onRefresh={() => onRefresh(ChannelId.dome)}
                  playerRef={domePlayerRef}
                  canLoad={loadedChannels?.includes(ChannelId.internal && ChannelId.front)}
                  onLoadEnd={() => {
                    setLoadedChannels((prev) => [...prev, ChannelId.dome]);
                  }}
                  videoLayer={
                    <Block className={videoLayerStyles}>
                      <StreamingChannelTag channelId={ChannelId.dome} />
                    </Block>
                  }
                  poster={posters[ChannelId.dome]}
                  streamType={StreamType.sub}
                  maxWidth={width}
                />
              )}
            </FlexColumn>
          );
        }}
      </AutoSizer>
    </FlexColumn>
  );
}

export default StreamingWindow;
